import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import EditAccessSheet from '../components/access-control/EditAccessSheet';
import ChangeFeedTab from '../components/access-control/ChangeFeedTab';
import RoleTemplatesTab from '../components/access-control/RoleTemplatesTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_e) { return iso; }
};

const ROLE_BADGE_COLORS = {
  Staff: 'bg-blue-100 text-blue-800',
  Player: 'bg-emerald-100 text-emerald-800',
  Parent: 'bg-amber-100 text-amber-800',
  Admin: 'bg-purple-100 text-purple-800',
};

const AccessControlPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [moduleLabels, setModuleLabels] = useState({});
  const [staffRoles, setStaffRoles] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [allAthletes, setAllAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [refreshTick, setRefreshTick] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // all | Staff | Player | Parent
  const [staffRoleFilter, setStaffRoleFilter] = useState('all');
  const [accessTypeFilter, setAccessTypeFilter] = useState('all'); // all | default | custom

  // Edit sheet state
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resettingLogs, setResettingLogs] = useState(false);

  useEffect(() => {
    if (user?.role !== 'Admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, modulesRes, rolesRes, athletesRes] = await Promise.all([
        axios.get(`${API}/permissions/users/all`),
        axios.get(`${API}/permissions/modules`),
        axios.get(`${API}/permissions/staff-roles`),
        axios.get(`${API}/permissions/athletes`),
      ]);
      setUsers(usersRes.data);
      setModules(modulesRes.data.modules);
      setModuleLabels(modulesRes.data.labels);
      setStaffRoles(rolesRes.data.role_types);
      setDefaultPermissions(rolesRes.data.default_permissions);
      setAllAthletes(athletesRes.data);
    } catch (_e) {
      toast.error('Failed to load access control data');
    } finally {
      setLoading(false);
    }
  };

  const triggerRefresh = () => {
    setRefreshTick((t) => t + 1);
    fetchData();
  };

  const openEdit = (userData) => {
    setEditUser(userData);
    setEditOpen(true);
  };

  const handleResetLogs = async () => {
    if (!window.confirm('This will permanently delete all legacy audit logs. Continue?')) return;
    setResettingLogs(true);
    try {
      const res = await axios.post(`${API}/permissions/logs/reset`);
      toast.success(`Cleared ${res.data.deleted_count} legacy log entries`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset logs');
    } finally {
      setResettingLogs(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (roleFilter !== 'all' && u.user.role !== roleFilter) return false;
      if (staffRoleFilter !== 'all' && u.staff_role_type !== staffRoleFilter) return false;
      if (accessTypeFilter === 'default' && u.has_overrides) return false;
      if (accessTypeFilter === 'custom' && !u.has_overrides) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${u.user.full_name || ''} ${u.user.email || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter, staffRoleFilter, accessTypeFilter]);

  const counts = useMemo(() => {
    return {
      Staff: users.filter((u) => u.user.role === 'Staff').length,
      Player: users.filter((u) => u.user.role === 'Player').length,
      Parent: users.filter((u) => u.user.role === 'Parent').length,
    };
  }, [users]);

  if (user?.role !== 'Admin') return null;

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Access Control
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Who can see what, for which athletes. Changes take effect immediately and invalidate active sessions.
            </p>
          </div>
          <Button
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
            onClick={handleResetLogs}
            disabled={resettingLogs}
            data-testid="reset-logs-btn"
          >
            {resettingLogs ? 'Clearing...' : 'Reset System Logs'}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="access-control-loading">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="users" data-testid="tab-users">
                Users ({counts.Staff + counts.Player + counts.Parent})
              </TabsTrigger>
              <TabsTrigger value="feed" data-testid="tab-feed">Recent Changes</TabsTrigger>
              <TabsTrigger value="roles" data-testid="tab-roles">Role Templates</TabsTrigger>
            </TabsList>

            {/* ============ USERS TAB (table) ============ */}
            <TabsContent value="users" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle>Users</CardTitle>
                      <CardDescription>Scan, filter and edit access for every user.</CardDescription>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Badge className={ROLE_BADGE_COLORS.Staff}>Staff {counts.Staff}</Badge>
                      <Badge className={ROLE_BADGE_COLORS.Player}>Players {counts.Player}</Badge>
                      <Badge className={ROLE_BADGE_COLORS.Parent}>Parents {counts.Parent}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      placeholder="Search by name or email..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="max-w-xs"
                      data-testid="user-search-input"
                    />
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                      <SelectTrigger className="w-40" data-testid="role-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="Staff">Staff</SelectItem>
                        <SelectItem value="Player">Player</SelectItem>
                        <SelectItem value="Parent">Parent</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={staffRoleFilter} onValueChange={setStaffRoleFilter}>
                      <SelectTrigger className="w-44" data-testid="staff-role-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Staff Roles</SelectItem>
                        {staffRoles.map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={accessTypeFilter} onValueChange={setAccessTypeFilter}>
                      <SelectTrigger className="w-44" data-testid="access-type-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Access Types</SelectItem>
                        <SelectItem value="default">Role Default</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    {(search || roleFilter !== 'all' || staffRoleFilter !== 'all' || accessTypeFilter !== 'all') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSearch(''); setRoleFilter('all'); setStaffRoleFilter('all'); setAccessTypeFilter('all'); }}
                        data-testid="clear-filters-btn"
                      >
                        Clear filters
                      </Button>
                    )}
                    <div className="ml-auto text-xs text-gray-500">
                      {filteredUsers.length} of {users.length}
                    </div>
                  </div>

                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[26%]">Name</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Athletes</TableHead>
                          <TableHead>Access Type</TableHead>
                          <TableHead>Last Updated</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                              No users match your filters.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredUsers.map((u) => (
                            <TableRow key={u.user.id} data-testid={`user-row-${u.user.id}`}>
                              <TableCell>
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${ROLE_BADGE_COLORS[u.user.role] || 'bg-gray-100 text-gray-700'}`}>
                                    {u.user.full_name?.charAt(0) || '?'}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{u.user.full_name}</p>
                                    <p className="text-xs text-gray-500 truncate">{u.user.email}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <Badge className={`${ROLE_BADGE_COLORS[u.user.role]} w-fit`}>{u.user.role}</Badge>
                                  {u.staff_role_type && (
                                    <Badge variant="outline" className="w-fit text-xs">{u.staff_role_type}</Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {u.user.role === 'Staff' ? (
                                  <span className="text-sm">{u.assigned_athletes?.length || 0}</span>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {u.has_overrides ? (
                                  <Badge className="bg-blue-100 text-blue-800">Custom ({u.override_count})</Badge>
                                ) : (
                                  <Badge variant="outline">Role Default</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="text-xs text-gray-500">{formatDate(u.updated_at)}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEdit(u)}
                                  data-testid={`edit-access-${u.user.id}`}
                                >
                                  Edit Access
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ============ RECENT CHANGES TAB ============ */}
            <TabsContent value="feed">
              <ChangeFeedTab refreshTick={refreshTick} onReverted={triggerRefresh} />
            </TabsContent>

            {/* ============ ROLE TEMPLATES TAB ============ */}
            <TabsContent value="roles">
              <RoleTemplatesTab
                staffRoles={staffRoles}
                defaultPermissions={defaultPermissions}
                moduleLabels={moduleLabels}
                modules={modules}
                onApplied={triggerRefresh}
              />
            </TabsContent>
          </Tabs>
        )}

        <EditAccessSheet
          open={editOpen}
          onOpenChange={setEditOpen}
          userData={editUser}
          moduleLabels={moduleLabels}
          staffRoles={staffRoles}
          defaultPermissions={defaultPermissions}
          allAthletes={allAthletes}
          onSaved={triggerRefresh}
        />
      </div>
    </Layout>
  );
};

export default AccessControlPanel;
